/**
 * Chart History Page
 * ===================
 * View and compare chart versions for a patient.
 */

import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  FileText,
  TrendingUp,
  TrendingDown,
  Minus,
  Calendar,
  Download,
} from 'lucide-react';

import { getChart, getPatientCharts, compareCharts } from '@/api/charts';
import { getPatient } from '@/api/patients';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { formatDate, getChartStatusClass } from '@/lib/utils';

export default function ChartHistory() {
  const { chartId } = useParams<{ chartId: string }>();
  const [selectedBaseline, setSelectedBaseline] = useState<string | null>(null);

  const { data: chart, isLoading: chartLoading } = useQuery({
    queryKey: ['chart', chartId],
    queryFn: () => getChart(chartId!),
    enabled: !!chartId,
  });

  const { data: patient } = useQuery({
    queryKey: ['patient', chart?.patient_id],
    queryFn: () => getPatient(chart!.patient_id),
    enabled: !!chart?.patient_id,
  });

  const { data: allCharts } = useQuery({
    queryKey: ['patient-charts', chart?.patient_id],
    queryFn: () => getPatientCharts(chart!.patient_id, false),
    enabled: !!chart?.patient_id,
  });

  const { data: comparison, isLoading: comparisonLoading } = useQuery({
    queryKey: ['chart-comparison', chartId, selectedBaseline],
    queryFn: () => compareCharts(chartId!, selectedBaseline!),
    enabled: !!chartId && !!selectedBaseline,
  });

  if (chartLoading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 bg-gray-200 rounded w-1/4" />
        <div className="h-64 bg-gray-200 rounded" />
      </div>
    );
  }

  if (!chart) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Chart not found</p>
        <Link to="/charts">
          <Button className="mt-4">Back to Charts</Button>
        </Link>
      </div>
    );
  }

  const olderCharts = allCharts?.filter((c) => 
    c.id !== chartId && new Date(c.exam_date) < new Date(chart.exam_date)
  ) || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to={`/charts/${chartId}`}
            className="text-gray-400 hover:text-gray-600"
          >
            <ArrowLeft className="w-6 h-6" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Chart History
            </h1>
            {patient && (
              <p className="text-gray-600">
                {patient.full_name} • {formatDate(chart.exam_date)}
              </p>
            )}
          </div>
        </div>
        <Button variant="outline">
          <Download className="w-4 h-4 mr-2" />
          Export Report
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Current Chart Summary */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Current Exam
          </h2>
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <Calendar className="w-5 h-5 text-gray-400" />
              <span>{formatDate(chart.exam_date)}</span>
            </div>
            <Badge className={getChartStatusClass(chart.status)}>
              {chart.status}
            </Badge>
            
            {chart.summary_stats && (
              <div className="pt-4 border-t space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Teeth Present:</span>
                  <span className="font-medium">{chart.summary_stats.teeth_present}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">BOP:</span>
                  <span className="font-medium">{chart.summary_stats.bop_percentage}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Avg PD:</span>
                  <span className="font-medium">{chart.summary_stats.avg_pocket_depth}mm</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Sites ≥4mm:</span>
                  <span className="font-medium">{chart.summary_stats.pockets_4mm_plus}</span>
                </div>
              </div>
            )}
          </div>
        </Card>

        {/* Comparison Selection */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Compare With
          </h2>
          {olderCharts.length > 0 ? (
            <div className="space-y-2">
              {olderCharts.map((c) => (
                <button
                  key={c.id}
                  onClick={() => setSelectedBaseline(c.id)}
                  className={`w-full text-left p-3 rounded-lg border transition-colors ${
                    selectedBaseline === c.id
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="font-medium text-gray-900">
                          {formatDate(c.exam_date)}
                        </p>
                        <p className="text-sm text-gray-500">
                          Version {c.version}
                          {c.is_baseline && ' • Baseline'}
                        </p>
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-sm">
              No previous charts available for comparison.
            </p>
          )}
        </Card>

        {/* Comparison Results */}
        <Card className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Changes
          </h2>
          {comparisonLoading ? (
            <div className="animate-pulse space-y-3">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-8 bg-gray-200 rounded" />
              ))}
            </div>
          ) : comparison ? (
            <div className="space-y-4">
              <ChangeItem
                label="Avg PD Change"
                value={comparison.changes_summary.avg_depth_change}
                unit="mm"
                reverseColor
              />
              <ChangeItem
                label="BOP Change"
                value={comparison.changes_summary.bop_change}
                unit="%"
                reverseColor
              />
              <div className="pt-4 border-t space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Improved Sites:</span>
                  <span className="text-green-600 font-medium flex items-center gap-1">
                    <TrendingDown className="w-4 h-4" />
                    {comparison.changes_summary.improved_sites}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Worsened Sites:</span>
                  <span className="text-red-600 font-medium flex items-center gap-1">
                    <TrendingUp className="w-4 h-4" />
                    {comparison.changes_summary.worsened_sites}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Stable Sites:</span>
                  <span className="text-gray-600 font-medium flex items-center gap-1">
                    <Minus className="w-4 h-4" />
                    {comparison.changes_summary.stable_sites}
                  </span>
                </div>
              </div>
            </div>
          ) : selectedBaseline ? (
            <p className="text-gray-500 text-sm">Loading comparison...</p>
          ) : (
            <p className="text-gray-500 text-sm">
              Select a previous chart to see changes.
            </p>
          )}
        </Card>
      </div>

      {/* Chart Timeline */}
      <Card className="p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Chart Timeline
        </h2>
        <div className="relative">
          <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200" />
          <div className="space-y-6">
            {allCharts?.map((c, index) => (
              <div key={c.id} className="relative flex items-start gap-4 pl-10">
                <div
                  className={`absolute left-2 w-5 h-5 rounded-full border-2 ${
                    c.id === chartId
                      ? 'bg-primary-500 border-primary-500'
                      : c.is_baseline
                      ? 'bg-blue-500 border-blue-500'
                      : 'bg-white border-gray-300'
                  }`}
                />
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900">
                        {formatDate(c.exam_date)}
                        {c.id === chartId && (
                          <span className="ml-2 text-primary-600">(Current)</span>
                        )}
                      </p>
                      <p className="text-sm text-gray-500">
                        {c.exam_type || 'Periodontal Exam'} • Version {c.version}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {c.is_baseline && <Badge variant="secondary">Baseline</Badge>}
                      <Badge className={getChartStatusClass(c.status)}>{c.status}</Badge>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Card>
    </div>
  );
}

// =============================================================================
// SUB-COMPONENTS
// =============================================================================

interface ChangeItemProps {
  label: string;
  value: number;
  unit: string;
  reverseColor?: boolean;
}

function ChangeItem({ label, value, unit, reverseColor }: ChangeItemProps) {
  const isPositive = value > 0;
  const isNegative = value < 0;
  const isNeutral = value === 0;

  let colorClass = 'text-gray-600';
  let Icon = Minus;

  if (reverseColor) {
    if (isNegative) {
      colorClass = 'text-green-600';
      Icon = TrendingDown;
    } else if (isPositive) {
      colorClass = 'text-red-600';
      Icon = TrendingUp;
    }
  } else {
    if (isPositive) {
      colorClass = 'text-green-600';
      Icon = TrendingUp;
    } else if (isNegative) {
      colorClass = 'text-red-600';
      Icon = TrendingDown;
    }
  }

  return (
    <div className="flex items-center justify-between">
      <span className="text-gray-600">{label}:</span>
      <span className={`font-medium flex items-center gap-1 ${colorClass}`}>
        <Icon className="w-4 h-4" />
        {isPositive && '+'}
        {value}
        {unit}
      </span>
    </div>
  );
}
